import { toCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { subYears } from 'date-fns';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import type { Gender, NamensTitel } from './enums/misc-enums.js';
import type { Namensvorsatz } from './enums/namensvorsatz.js';
import type { Namenszusatz } from './enums/namenszusatz.js';
import { validateJobTicketCancelDate, validateJobTicketOrderDate } from './utils/util-validation.js';

/**
 * @entity JobTicketWrapper
 */
/** minimum age for a job ticket, mirrors the `validation_birthday_12` message */
const MINIMUM_JOB_TICKET_AGE_IN_YEARS = 12;

@Entity()
export class JobTicketWrapper extends AdornisEntity {
  static override _class = 'JobTicketWrapper';

  constructor(doc: PartialEntityData<JobTicketWrapper>) {
    super(doc);
  }

  @Field(type => String)
  titel: Maybe<NamensTitel>;

  @validate(sgNonOptional({ allowFalsy: false }))
  @Field(type => String)
  vorname: Maybe<string>;

  @validate(sgNonOptional())
  @Field(type => String)
  nachname: Maybe<string>;

  @Field(type => String)
  namenszusatz: Maybe<Namenszusatz>;

  @Field(type => String)
  vorsatzWort: Maybe<Namensvorsatz>;

  @validate(v => {
    const value = v.value;
    if (!value) throw new ValidationError('', { key: v.key, translationKey: 'validation_non_optional' });
    if (!(value instanceof Date)) return;

    // canonical calendar days, so the cutoff is built on the UTC grid
    const today = todayAsCalendarDay();
    const earliestAllowedBirthday = toCalendarDay(subYears(today, MINIMUM_JOB_TICKET_AGE_IN_YEARS));
    if (value > earliestAllowedBirthday)
      throw new ValidationError('', { key: v.key, translationKey: 'validation_birthday_12' });
  })
  @Field(type => Date)
  geburtsDatum: Maybe<Date>;

  @validate(sgNonOptional())
  @Field(type => String)
  geschlecht!: Gender;

  @validate(sgNonOptional())
  @Field(type => String)
  streetAndNumber: Maybe<string>;

  @validate(sgNonOptional())
  @Field(type => String)
  zip: Maybe<string>;

  @validate(sgNonOptional())
  @Field(type => String)
  city: Maybe<string>;

  @validate(sgNonOptional())
  @Field(type => String)
  country: Maybe<string>;

  @validate(sgNonOptional())
  @Field(type => Boolean, { default: v => v ?? false })
  jobTicketKenntnis: Maybe<boolean>;

  @validate(sgNonOptional())
  @Field(type => Boolean, { default: v => v ?? false })
  jobTicketBestaetigt: Maybe<boolean>;

  @validate(sgNonOptional())
  @Field(type => Boolean, { default: v => v ?? false })
  jobTicketDatenweitergabe: Maybe<boolean>;

  // If we refactor Job-Ticket into a section, this should be renamed to jobTicketBestellt
  @Field(type => Boolean, { default: v => v ?? false })
  deutschlandTicketBestellt: Maybe<boolean>;

  @validate(options => {
    const value = options.value;
    if (!value) throw new ValidationError('', { key: options.key, translationKey: 'validation_non_optional' });
    if (!(value instanceof Date)) return;
    const target = options.target;
    if (!(target instanceof JobTicketWrapper)) return;

    /** skip validation when there are no changes */
    if (target.doc?.jobTicketBestellDatum === value) return;
    validateJobTicketOrderDate(new Date(), value, options.key);
  })
  @Field(type => Date)
  jobTicketBestellDatum: Maybe<Date>;

  @validate(options => {
    const value = options.value;
    if (!value) return;
    if (!(value instanceof Date)) return;
    const target = options.target;
    if (!(target instanceof JobTicketWrapper)) return;
    validateJobTicketCancelDate(new Date(), target.jobTicketBestellDatum, value, options.key);
  })
  @Field(type => Date)
  jobTicketKuendigungsDatum: Maybe<Date>;

  @Field(type => Date)
  jobTicketExportiertAm: Maybe<Date>;
}
